import { saveAs } from 'file-saver';
import React from 'react';

import {
  cancelDocumentMetadataImportJob,
  downloadDocumentsMetadataImportResultsExcelFile,
  setDocumentMetadataImportJobAcknowledge,
} from '~/api/documentMetadata';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { BulkImportDocumentMetadataResultType } from '~/enums/BulkImportDocumentMetadataResultType';
import { BulkImportStatusType } from '~/enums/BulkImportStatusType';
import { CodedErrorCodeType } from '~/enums/CodedErrorCodeType';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { ERROR, SUCCESS } from '~/types/toast.types';
import Link from '~/ui/components/Link/Link';
import { parseApiError } from '~/utils/parseApiError';

export const useBarItemBaseProps = () => {
  const intl = useIntl();
  const formatDate = useFormatDate();

  const handleApiError = (errorResponse) => {
    const error = parseApiError(errorResponse);
    showToast(ERROR, error.message);
  };

  const handleImportErrorsFileDownloadClick = async (importId, clientId) => {
    const resultType = BulkImportDocumentMetadataResultType.ProcessErrors;
    try {
      const result = await downloadDocumentsMetadataImportResultsExcelFile({
        clientId,
        importId,
        resultType,
      });

      showToast(
        SUCCESS,
        intl.formatMessage(
          {
            id: 'UseBarItemBaseProps.fileDownloadSuccess',
            defaultMessage: '{resultType} file downloaded successfully.',
            description:
              'Success message when file is downloaded. Example: resultType = "errors" or "results"',
          },
          { resultType },
        ),
      );
      const timestamp = formatDate(new Date(), 'utc');
      const filename = `evisort-documents-metadata-${resultType}-${timestamp}.xlsx`;
      saveAs(result, filename);
    } catch (errorResponse) {
      handleApiError(errorResponse);
    }
  };

  const handleImportProcessCancelAction = async (clientId, importData) => {
    const { importId } = importData;
    try {
      await cancelDocumentMetadataImportJob({ clientId, importId });
      showToast(
        SUCCESS,
        intl.formatMessage({
          id: 'UseBarItemBaseProps.cancelSuccessMessage',
          defaultMessage: 'Canceled import job successfully.',
          description: 'Success message when import job is canceled',
        }),
      );
    } catch (errorResponse) {
      handleApiError(
        errorResponse,
        CodedErrorCodeType.BulkImportErrorCancellingJob,
      );
    }
  };

  const handleImportJobAcknowledgeClick = async ({
    clientId,
    importData,
    handleAcknowledgeImportJobUpdate,
  }) => {
    const { importId } = importData;
    try {
      await setDocumentMetadataImportJobAcknowledge({ clientId, importId });
      showToast(
        SUCCESS,
        intl.formatMessage({
          id: 'UseBarItemBaseProps.acknowledgeSuccessMessage',
          defaultMessage: 'Acknowledged import job successfully.',
          description: 'Success message when import job is acknowledged',
        }),
      );
      handleAcknowledgeImportJobUpdate(importId);
    } catch (errorResponse) {
      handleApiError(
        errorResponse,
        CodedErrorCodeType.BulkImportErrorAcknowledgingJob,
      );
    }
  };

  const getErrorMessage = (errorMessagePrefix, errors) =>
    intl.formatMessage(
      {
        id: 'UseBarItemBaseProps.errorMessage',
        defaultMessage:
          '{errorMessagePrefix} {errors} documents metadata failed to import.',
        description:
          'Generic error message for failed imports. Example: errorMessagePrefix = "Processing failed -", errors = "5"',
      },
      { errorMessagePrefix, errors },
    );

  const getErrorDownloadMessage = ({
    importId,
    clientId,
    errorMessagePrefix,
    errors,
  }) => (
    <>
      {getErrorMessage(errorMessagePrefix, errors)}
      <Link
        variant="body2"
        onClick={() => handleImportErrorsFileDownloadClick(importId, clientId)}
      >
        <FormattedMessage
          id="UseBarItemBaseProps.downloadErrorLink"
          defaultMessage="Download the error list"
          description="Link text to download error list"
        />
      </Link>{' '}
      <FormattedMessage
        id="UseBarItemBaseProps.fixErrorsInstruction"
        defaultMessage="to fix errors."
        description="Instruction to fix errors"
      />
    </>
  );

  const getFinishedStatusMessage = ({ importId, counters }, clientId) => {
    const { processed, total, errors } = counters;
    const finishedImportMessage = intl.formatMessage(
      {
        id: 'UseBarItemBaseProps.finishedImportMessage',
        defaultMessage: 'Processed {processed} documents metadata of {total}.',
        description:
          'Message when import is finished. Example: processed = "95", total = "100"',
      },
      { processed, total },
    );
    if (errors === 0) return finishedImportMessage;

    return getErrorDownloadMessage({
      importId,
      clientId,
      errorMessagePrefix: finishedImportMessage,
      errors,
    });
  };

  const getCancelledStatusMessage = ({ counters }) => {
    const { processed, total, errors } = counters;
    const cancelledImportMessage = intl.formatMessage(
      {
        id: 'UseBarItemBaseProps.cancelledImportMessage',
        defaultMessage:
          'Canceled - Processed {processed} documents metadata of {total}.',
        description:
          'Message when import is canceled. Example: processed = "25", total = "100"',
      },
      { processed, total },
    );
    if (errors === 0) return cancelledImportMessage;

    return getErrorMessage(cancelledImportMessage, errors);
  };

  const barItemBaseProps = {
    [BulkImportStatusType.New]: {
      getIcon: () => 'loading',
      getBarItemText: () => (
        <FormattedMessage
          id="UseBarItemBaseProps.queueMessage"
          defaultMessage="Documents metadata import in queue."
          description="Message when import is in queue"
        />
      ),
      getActions: ({ clientId, importData }) => [
        {
          onClick: () => handleImportProcessCancelAction(clientId, importData),
          text: intl.formatMessage({
            id: 'UseBarItemBaseProps.cancelImportButton',
            defaultMessage: 'Cancel Import',
            description: 'Button to cancel import',
          }),
        },
      ],
    },
    [BulkImportStatusType.Failed]: {
      getIcon: () => 'danger',
      getBarItemText: () => (
        <FormattedMessage
          id="UseBarItemBaseProps.failedMessage"
          defaultMessage="Documents metadata import failed."
          description="Message when import fails"
        />
      ),
      getActions: () => [],
      handleImportJobAcknowledgeClick,
    },
    [BulkImportStatusType.Running]: {
      getIcon: () => 'loading',
      getBarItemText: ({ importData: { counters } }) => (
        <FormattedMessage
          id="UseBarItemBaseProps.runningMessage"
          defaultMessage="Processed {processed} documents metadata of {total}."
          description='Message when import is running. Example: processed = "45", total = "100"'
          values={{ processed: counters.processed, total: counters.total }}
        />
      ),
      getActions: ({ clientId, importData }) => [
        {
          onClick: () => handleImportProcessCancelAction(clientId, importData),
          text: intl.formatMessage({
            id: 'UseBarItemBaseProps.cancelImportButton2',
            defaultMessage: 'Cancel Import',
            description: 'Button to cancel import',
          }),
        },
      ],
    },
    [BulkImportStatusType.Finished]: {
      getIcon: ({ errors }) => (errors === 0 ? 'success' : 'warning'),
      getBarItemText: ({ clientId, importData }) =>
        getFinishedStatusMessage(importData, clientId),
      getActions: () => [],
      handleImportJobAcknowledgeClick,
    },
    [BulkImportStatusType.Cancelled]: {
      getIcon: () => 'warning',
      getBarItemText: ({ clientId, importData }) =>
        getCancelledStatusMessage(importData, clientId),
      getActions: () => [],
      handleImportJobAcknowledgeClick,
    },
  };

  return barItemBaseProps;
};
